// components/project/SalItemTimelineModal.jsx
// R.5.2 §3 — Modal read-only con la timeline cronologica di una lavorazione.
// Consuma GET /api/sal/{id}/timeline. Zero editing, ordinamento asc.

import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Clock, Plus, RefreshCw, Ban, RotateCcw, Link2, FileText, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { salApi } from '../../lib/api';

const formatCurrency = (v) => {
  const n0 = Number(v ?? 0);
  const n = Math.round(n0 * 100) === 0 ? 0 : n0;
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return String(iso);
  }
};

const EVENT_ICONS = {
  sal_item_created: { Icon: Plus, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  sal_update: { Icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-100' },
  sal_item_stralciata: { Icon: Ban, color: 'text-red-600', bg: 'bg-red-100' },
  sal_item_stralcio_residuo: { Icon: Ban, color: 'text-orange-600', bg: 'bg-orange-100' },
  sal_item_reactivated: { Icon: RotateCcw, color: 'text-emerald-700', bg: 'bg-emerald-100' },
  extra_linked: { Icon: Link2, color: 'text-purple-600', bg: 'bg-purple-100' },
};

const STATE_META = {
  stralciata: { label: 'Stralciata', color: 'bg-red-100 text-red-700 border-red-200', Icon: Ban },
  riattivata: { label: 'Riattivata', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: RotateCcw },
  completata: { label: 'Completata', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  in_corso: { label: 'In corso', color: 'bg-blue-100 text-blue-700 border-blue-200', Icon: RefreshCw },
  attiva: { label: 'Attiva', color: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Clock },
};

// Paginazione client-side: 25 eventi per pagina
const PAGE_SIZE = 25;

const SalItemTimelineModal = ({ open, onOpenChange, salItemId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!open || !salItemId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setPage(0);
      try {
        const res = await salApi.getTimeline(salItemId);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 403) {
            setError({ code: 403, message: 'Non autorizzato a visualizzare la storia di questa lavorazione.' });
          } else if (e.response?.status === 404) {
            setError({ code: 404, message: 'Lavorazione non trovata.' });
          } else {
            setError({ code: e.response?.status || 0, message: e.response?.data?.detail || 'Errore nel caricamento' });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, salItemId]);

  const events = data?.events || [];
  const pageCount = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const visibleEvents = useMemo(
    () => events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [events, page]
  );

  const stateMeta = STATE_META[data?.current_state] || STATE_META.attiva;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="sal-timeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600" />
            Storia lavorazione
          </DialogTitle>
          <DialogDescription>
            Ciclo di vita cronologico ricostruito dagli eventi registrati (read-only).
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-[#737373]" data-testid="sal-timeline-loading">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Caricamento cronologia…
          </div>
        )}

        {error && (
          <div
            className="my-4 p-3 rounded-md border border-red-200 bg-red-50 flex items-start gap-3"
            data-testid="sal-timeline-error"
          >
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="text-sm text-red-800">
              <div className="font-semibold">Impossibile caricare la storia</div>
              <div className="text-xs">{error.message}</div>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Header voce + stato corrente */}
            <div className="border-b border-[#EDEDED] pb-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <FileText className="w-4 h-4 text-[#737373]" />
                <span className="font-mono text-sm font-semibold text-[#0078C8]" data-testid="sal-timeline-code">
                  {data.code || '—'}
                </span>
                <span
                  className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full border ${stateMeta.color}`}
                  data-testid="sal-timeline-current-state"
                >
                  <stateMeta.Icon className="w-3 h-3 inline mr-1 -mt-0.5" />
                  {data.current_state_label || stateMeta.label}
                </span>
                {typeof data.completion_percentage === 'number' && (
                  <span className="text-xs text-[#737373]">
                    Avanzamento: <strong>{Number(data.completion_percentage).toFixed(1)}%</strong>
                  </span>
                )}
                <span className="text-xs text-[#737373] ml-auto">
                  {data.events_count} eventi
                </span>
              </div>
              {data.description && (
                <div className="text-sm text-[#4B5563] mt-1">{data.description}</div>
              )}
            </div>

            {/* Timeline eventi (ordinamento asc dal backend) */}
            <div className="flex-1 overflow-y-auto pr-1" data-testid="sal-timeline-list">
              {events.length === 0 ? (
                <div className="py-6 text-center text-sm text-[#9CA3AF]" data-testid="sal-timeline-empty">
                  Nessun evento registrato per questa lavorazione.
                </div>
              ) : (
                <ol className="relative border-l-2 border-[#EDEDED] pl-4 space-y-4">
                  {visibleEvents.map((ev, idx) => {
                    const icon = EVENT_ICONS[ev.event_type] || { Icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100' };
                    const IconComp = icon.Icon;
                    const isLatestOverall = (page === pageCount - 1) && (idx === visibleEvents.length - 1);
                    return (
                      <li
                        key={`${ev.event_type}-${ev.created_at}-${idx}`}
                        className="relative"
                        data-testid={`sal-timeline-event-${page * PAGE_SIZE + idx}`}
                      >
                        <span
                          className={`absolute -left-[26px] top-0 flex items-center justify-center w-6 h-6 rounded-full ring-2 ring-white ${icon.bg}`}
                        >
                          <IconComp className={`w-3.5 h-3.5 ${icon.color}`} />
                        </span>
                        <div
                          className={`rounded-md border ${isLatestOverall ? 'border-purple-300 bg-purple-50' : 'border-[#EDEDED] bg-white'} p-3`}
                        >
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="font-medium text-sm text-[#3C3C3C]">
                              {ev.label || ev.title || ev.event_type}
                            </div>
                            <div className="text-xs text-[#737373] font-mono">
                              {formatDateTime(ev.created_at)}
                            </div>
                          </div>
                          {ev.title && ev.title !== ev.label && (
                            <div className="text-xs text-[#4B5563] mt-1">{ev.title}</div>
                          )}
                          {ev.description && (
                            <div className="text-xs text-[#4B5563] mt-1 whitespace-pre-wrap">{ev.description}</div>
                          )}
                          <EventMeta metadata={ev.metadata} eventType={ev.event_type} />
                          <div className="text-[11px] text-[#9CA3AF] mt-2">
                            Operatore: <span className="text-[#4B5563]">{ev.actor || '—'}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Paginazione */}
            {pageCount > 1 && (
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#EDEDED]">
                <div className="text-xs text-[#737373]">
                  Pagina {page + 1} di {pageCount}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    data-testid="sal-timeline-prev"
                  >
                    Precedente
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    data-testid="sal-timeline-next"
                  >
                    Successiva
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Renderer strutturato dei metadata dell'evento con le colonne standard
 * (quantità contrattuale, eseguita, stralciata, %, importo, motivo, categoria, variante).
 */
const EventMeta = ({ metadata, eventType }) => {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  const rows = [];
  const push = (label, value) => {
    if (value === undefined || value === null || value === '') return;
    rows.push([label, value]);
  };

  push('Q.tà contrattuale', metadata.contract_quantity);
  push('Q.tà eseguita', metadata.quantity_executed_at_stralcio ?? metadata.completed_quantity);
  push('Q.tà stralciata', metadata.quantity_stralciata);
  push('%', metadata.new_percentage != null ? `${Number(metadata.new_percentage).toFixed(1)}%` : undefined);
  const impact = metadata.amount_stralciato ?? metadata.stralcio_amount ?? metadata.extra_total;
  if (impact != null) push('Importo', formatCurrency(impact));
  push('Motivo', metadata.reason);
  if (metadata.category) {
    push('Categoria', metadata.category);
  }
  if (metadata.replacement_extra_id) {
    push('Variante', `${String(metadata.replacement_extra_id).slice(0, 8)}…`);
  }
  if (metadata.extra_number) {
    push('Variante', metadata.extra_number);
  }
  if (metadata.stralcio_scope) {
    push('Scope', metadata.stralcio_scope === 'full' ? 'Totale' : 'Residuo');
  }
  if (rows.length === 0) return null;

  return (
    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]" data-testid="sal-timeline-event-meta">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-2 border-b border-dashed border-[#F3F4F6] pb-0.5">
          <span className="text-[#9CA3AF]">{k}</span>
          <span className="text-[#4B5563] font-mono truncate max-w-[60%]" title={String(v)}>{String(v)}</span>
        </div>
      ))}
    </div>
  );
};

export default SalItemTimelineModal;
